import {
    Box,
    Heading,
    Button,
    Card,
    CardBody,
    Flex,
    Badge,
    Text,
    Icon,
    HStack,
    VStack,
    Spinner,
    Center,
    useDisclosure,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalCloseButton,
    FormControl,
    FormLabel,
    Input,
    Select,
    useToast,
    SimpleGrid,
    Progress,
} from '@chakra-ui/react';
import { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import {
    MdUpload,
    MdDescription,
    MdPictureAsPdf,
    MdTableChart,
} from 'react-icons/md';


import { useDocuments, useUploadDocument, useProjects } from '@/services/hooks';

const fileTypeIcons: Record<string, { icon: React.ElementType; color: string }> = {
    'application/pdf': { icon: MdPictureAsPdf, color: 'red.400' },
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': { icon: MdTableChart, color: 'green.400' },
    'text/csv': { icon: MdTableChart, color: 'green.400' },
    default: { icon: MdDescription, color: 'blue.400' },
};

export function Documents() {
    const toast = useToast();
    const { isOpen, onOpen, onClose } = useDisclosure();

    const { data: documents, isLoading, refetch } = useDocuments();
    const { data: projects } = useProjects();
    const uploadMutation = useUploadDocument();

    const [uploadFile, setUploadFile] = useState<File | null>(null);
    const [metadata, setMetadata] = useState({ projectId: '', tags: '' });
    const [uploadProgress, setUploadProgress] = useState(0);

    const onDrop = useCallback((acceptedFiles: File[]) => {
        if (acceptedFiles.length > 0) {
            setUploadFile(acceptedFiles[0]);
            onOpen();
        }
    }, [onOpen]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'application/pdf': ['.pdf'],
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
            'text/csv': ['.csv'],
            'image/*': ['.png', '.jpg', '.jpeg'],
        },
        maxSize: 50 * 1024 * 1024, // 50MB
    });

    const handleUpload = async () => {
        if (!uploadFile) return;

        try {
            // Simulate progress
            const progressInterval = setInterval(() => {
                setUploadProgress((prev) => Math.min(prev + 10, 90));
            }, 200);

            await uploadMutation.mutateAsync({
                file: uploadFile,
                metadata: {
                    projectId: metadata.projectId ? Number(metadata.projectId) : undefined,
                    tags: metadata.tags.split(',').map((t) => t.trim()).filter(Boolean),
                },
            });

            clearInterval(progressInterval);
            setUploadProgress(100);

            toast({
                title: 'Document uploaded',
                description: 'AI processing will begin shortly.',
                status: 'success',
                duration: 3000,
            });

            setTimeout(() => {
                onClose();
                setUploadFile(null);
                setMetadata({ projectId: '', tags: '' });
                setUploadProgress(0);
                refetch();
            }, 500);
        } catch (error) {
            toast({
                title: 'Upload failed',
                status: 'error',
                duration: 3000,
            });
            setUploadProgress(0);
        }
    };

    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    };

    const formatDate = (date: string) => {
        return new Date(date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    const getFileIcon = (mimeType: string) => {
        return fileTypeIcons[mimeType] || fileTypeIcons.default;
    };

    // Mock data for display
    const mockDocuments = [
        {
            id: 1,
            filename: 'Bid_Tabulation_MLK_Drive_2026.pdf',
            mime_type: 'application/pdf',
            size_bytes: 2458624,
            status: 'processed',
            project_title: 'MLK Drive Reconstruction',
            created_at: new Date().toISOString(),
            items_extracted: 45,
        },
        {
            id: 2,
            filename: 'Unit_Prices_Highway_Resurfacing.xlsx',
            mime_type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            size_bytes: 156800,
            status: 'processed',
            project_title: '2026 Resurfacing Program',
            created_at: new Date(Date.now() - 86400000).toISOString(),
            items_extracted: 128,
        },
        {
            id: 3,
            filename: 'Storm_Sewer_Bid_Tab.pdf',
            mime_type: 'application/pdf',
            size_bytes: 1845000,
            status: 'processing',
            project_title: null,
            created_at: new Date(Date.now() - 3600000).toISOString(),
            items_extracted: null,
        },
    ];

    const displayDocuments = documents?.items || mockDocuments;

    return (
        <Box>
            <Flex justify="space-between" align="center" mb={6}>
                <Heading size="lg">Documents</Heading>
            </Flex>

            {/* Upload Dropzone */}
            <Card
                mb={6}
                {...getRootProps()}
                cursor="pointer"
                borderStyle="dashed"
                borderWidth="2px"
                borderColor={isDragActive ? 'brand.400' : 'navy.600'}
                bg={isDragActive ? 'navy.700' : 'transparent'}
                transition="all 0.2s"
                _hover={{ borderColor: 'brand.400' }}
            >
                <CardBody py={8}>
                    <input {...getInputProps()} />
                    <VStack spacing={3}>
                        <Icon as={MdUpload} boxSize={12} color={isDragActive ? 'brand.400' : 'gray.500'} />
                        <Text color={isDragActive ? 'brand.400' : 'gray.400'} fontWeight="medium">
                            {isDragActive ? 'Drop file here...' : 'Drag & drop a document, or click to select'}
                        </Text>
                        <Text fontSize="sm" color="gray.500">
                            Supported: PDF, Excel, CSV, Images (max 50MB)
                        </Text>
                    </VStack>
                </CardBody>
            </Card>

            {/* Documents Grid */}
            {isLoading ? (
                <Center py={12}>
                    <Spinner size="lg" color="brand.500" />
                </Center>
            ) : displayDocuments.length === 0 ? (
                <Card>
                    <CardBody>
                        <Center py={12}>
                            <VStack>
                                <Icon as={MdDescription} boxSize={12} color="gray.500" />
                                <Text color="gray.500">No documents uploaded yet</Text>
                            </VStack>
                        </Center>
                    </CardBody>
                </Card>
            ) : (
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                    {displayDocuments.map((doc: any) => {
                        const fileIcon = getFileIcon(doc.mime_type);
                        return (
                            <Card key={doc.id} _hover={{ transform: 'translateY(-2px)', shadow: 'lg' }} transition="all 0.2s">
                                <CardBody>
                                    <Flex gap={4}>
                                        <Box
                                            p={3}
                                            bg="navy.700"
                                            borderRadius="lg"
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                        >
                                            <Icon as={fileIcon.icon} boxSize={8} color={fileIcon.color} />
                                        </Box>
                                        <Box flex="1" overflow="hidden">
                                            <Text fontWeight="medium" noOfLines={1} title={doc.filename}>
                                                {doc.filename}
                                            </Text>
                                            <Text fontSize="sm" color="gray.500" noOfLines={1}>
                                                {doc.project_title || 'No project assigned'}
                                            </Text>
                                            <HStack mt={2} spacing={2}>
                                                <Text fontSize="xs" color="gray.500">
                                                    {formatFileSize(doc.size_bytes)}
                                                </Text>
                                                <Text fontSize="xs" color="gray.500">•</Text>
                                                <Text fontSize="xs" color="gray.500">
                                                    {formatDate(doc.created_at)}
                                                </Text>
                                            </HStack>
                                            <HStack mt={2}>
                                                <Badge
                                                    colorScheme={doc.status === 'processed' ? 'green' : doc.status === 'processing' ? 'yellow' : 'gray'}
                                                >
                                                    {doc.status}
                                                </Badge>
                                                {doc.items_extracted && (
                                                    <Badge colorScheme="blue" variant="subtle">
                                                        {doc.items_extracted} items
                                                    </Badge>
                                                )}
                                            </HStack>
                                        </Box>
                                    </Flex>
                                </CardBody>
                            </Card>
                        );
                    })}
                </SimpleGrid>
            )}

            {/* Upload Modal */}
            <Modal isOpen={isOpen} onClose={onClose}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Upload Document</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        {uploadFile && (
                            <Card mb={4} bg="navy.700">
                                <CardBody py={3}>
                                    <Flex align="center" gap={3}>
                                        <Icon as={getFileIcon(uploadFile.type).icon} color={getFileIcon(uploadFile.type).color} />
                                        <Box flex="1">
                                            <Text fontSize="sm" fontWeight="medium" noOfLines={1}>
                                                {uploadFile.name}
                                            </Text>
                                            <Text fontSize="xs" color="gray.500">
                                                {formatFileSize(uploadFile.size)}
                                            </Text>
                                        </Box>
                                    </Flex>
                                </CardBody>
                            </Card>
                        )}

                        {uploadProgress > 0 && (
                            <Progress
                                value={uploadProgress}
                                size="sm"
                                colorScheme="brand"
                                mb={4}
                                borderRadius="full"
                            />
                        )}

                        <FormControl mb={4}>
                            <FormLabel>Link to Project (optional)</FormLabel>
                            <Select
                                placeholder="Select project..."
                                value={metadata.projectId}
                                onChange={(e) => setMetadata({ ...metadata, projectId: e.target.value })}
                            >
                                {(projects?.items || []).map((project: any) => (
                                    <option key={project.id} value={project.id}>
                                        {project.title}
                                    </option>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl>
                            <FormLabel>Tags (comma-separated)</FormLabel>
                            <Input
                                placeholder="e.g., bid-tab, 2026, highway"
                                value={metadata.tags}
                                onChange={(e) => setMetadata({ ...metadata, tags: e.target.value })}
                            />
                        </FormControl>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={onClose}>
                            Cancel
                        </Button>
                        <Button
                            colorScheme="brand"
                            onClick={handleUpload}
                            isLoading={uploadMutation.isPending}
                            isDisabled={!uploadFile}
                        >
                            Upload
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
}
